import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, Chip, Avatar,
  Skeleton, Switch, FormControlLabel, Alert, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  LocationOn as LocationIcon, Wifi as WifiIcon,
  SettingsInputComponent as StationIcon,
  TabletMac as TabletIcon,
} from '@mui/icons-material';
import { locationAPI } from '../services/api';
import toast from 'react-hot-toast';

function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', numStations: 3, isActive: true });
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => { fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const res = await locationAPI.getAll();
      setLocations(res.data.data || []);
    } catch (error) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setForm({ name: location.name, address: location.address || '', numStations: location.numStations, isActive: location.isActive });
    } else {
      setEditingLocation(null);
      setForm({ name: '', address: '', numStations: 3, isActive: true });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingLocation) {
        await locationAPI.update(editingLocation._id, form);
        toast.success('Location updated');
      } else {
        await locationAPI.create(form);
        toast.success('Location created with stations');
      }
      setDialogOpen(false);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    try {
      await locationAPI.delete(id);
      toast.success('Location deleted');
      setDeleteConfirm(null);
      fetchLocations();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 3, flexWrap: 'wrap', gap: 2,
      }}>
        <Box>
          <Typography variant={isSmall ? 'h5' : 'h4'} sx={{ fontWeight: 700, color: '#0f172a' }}>
            Locations
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage health camp locations and their station setup
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Add Location
        </Button>
      </Box>

      {loading ? (
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {[1, 2, 3].map(i => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Card><CardContent><Skeleton height={120} /></CardContent></Card>
            </Grid>
          ))}
        </Grid>
      ) : locations.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <LocationIcon sx={{ fontSize: 64, color: '#67e8f9', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No locations yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add your first health camp location to get started
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Create First Location
          </Button>
        </Card>
      ) : (
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {locations.map((loc, index) => (
            <Grid item xs={12} sm={6} lg={4} key={loc._id}>
              <Card sx={{
                position: 'relative', overflow: 'hidden',
                transition: 'all 0.3s ease',
                animation: `fade-in-up 0.5s ease-out ${index * 0.1}s both`,
                '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 30px rgba(8,145,178,0.12)' },
              }}>
                {/* Top gradient bar */}
                <Box sx={{
                  height: 4,
                  background: loc.isActive
                    ? 'linear-gradient(90deg, #0891b2, #06b6d4, #22d3ee)'
                    : '#e2e8f0',
                }} />
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                      <Avatar sx={{
                        width: 44, height: 44, flexShrink: 0,
                        background: loc.isActive
                          ? 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)'
                          : '#e2e8f0',
                      }}>
                        <LocationIcon />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>{loc.name}</Typography>
                        {loc.address && (
                          <Typography variant="caption" color="text.secondary" noWrap>{loc.address}</Typography>
                        )}
                      </Box>
                    </Box>
                    <Chip
                      label={loc.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={loc.isActive ? 'success' : 'default'}
                      sx={{ flexShrink: 0 }}
                    />
                  </Box>

                  {/* Stats */}
                  <Box sx={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1,
                    p: 2, borderRadius: 3, bgcolor: '#f0f9ff', mb: 2,
                    border: '1px solid #e0f2fe',
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <StationIcon sx={{ fontSize: 20, color: '#0891b2', mb: 0.5 }} />
                      <Typography variant="h6" fontWeight={700}>{loc.stationCount || loc.numStations}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Stations</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <TabletIcon sx={{ fontSize: 20, color: '#0ea5e9', mb: 0.5 }} />
                      <Typography variant="h6" fontWeight={700}>{loc.tabletCount ?? 0}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Tablets</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <WifiIcon sx={{ fontSize: 20, color: (loc.onlineTablets ?? 0) > 0 ? '#10b981' : '#94a3b8', mb: 0.5 }} />
                      <Typography variant="h6" fontWeight={700}>{loc.onlineTablets ?? 0}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Online</Typography>
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small" variant="outlined" startIcon={<EditIcon />}
                      onClick={() => handleOpenDialog(loc)} sx={{ flex: 1 }}
                    >
                      Edit
                    </Button>
                    <IconButton
                      size="small" color="error"
                      onClick={() => setDeleteConfirm(loc._id)}
                      sx={{ border: '1px solid', borderColor: 'error.light', borderRadius: 2 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen} onClose={() => setDialogOpen(false)}
        maxWidth="sm" fullWidth
        fullScreen={isSmall}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingLocation ? 'Edit Location' : 'Add New Location'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              label="Location Name" fullWidth required
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Village Health Camp - Andheri"
            />
            <TextField
              label="Address" fullWidth multiline rows={2}
              value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Full address of the camp site"
            />
            {!editingLocation && (
              <TextField
                label="Number of Stations" type="number" fullWidth
                value={form.numStations}
                onChange={(e) => setForm({ ...form, numStations: Math.max(1, parseInt(e.target.value) || 1) })}
                helperText="Stations will be auto-created. You can configure tests later."
                InputProps={{ inputProps: { min: 1, max: 10 } }}
              />
            )}
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} color="primary" />}
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>
            {editingLocation ? 'Update' : 'Create Location'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Location?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This will delete the location and all its stations. This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Locations;
